import {
	LuBookCheck,
	LuCheck,
	LuChevronRight,
	LuCircleCheck,
	LuCopy,
	LuLoaderCircle,
	LuLock,
	LuScanText,
	LuShare,
	LuShield,
} from 'react-icons/lu'
import Modal from './Modal'
import { Skeleton } from '@mui/material'
import { useState, useEffect, useCallback } from 'react'
import Button from '../Buttons'
import { GraderRubric } from '../Navbar/Grader'
import ScoreBar from '../Grader/Scorebar'
import { useSelector } from 'react-redux'
import { selectDocumentState } from '../../redux/docSlice'
import { selectUser } from '../../redux/systemSlice'
import { useDocId } from '../../hooks/docID'
import { useAuth } from '../../contexts/Auth'
import { htmlToString } from '../../helpers/utility'
import * as Sentry from '@sentry/react'
import useIsPremium from '../../hooks/useIsPremium'
import { accountModalAtom } from '../../Pages/Create/Create'
import { useAtom } from 'jotai'
import { DocumentState } from '../../redux/types'
import { formatReferenceAsString } from '../../helpers/strings'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'

const CompleteModal = ({ open, closeModal }: { open: boolean; closeModal: () => void }) => {
	const defaultPlagiarismTabFF = useFeatureFlagVariantKey(FEATURE_FLAGS.DEFAULT_PLAGIARISM_TAB)
	const completeToSignupFF = useFeatureFlagVariantKey(FEATURE_FLAGS.COMPLETE_TO_SIGNUP)
	const isPremium = useIsPremium()
	const [loading, setLoading] = useState(true)
	const [openBox, setOpenBox] = useState<number | null>(null)
	const [gradeResult, setGradeResult] = useState<GraderRubric | null>(null)
	const [gradeLoading, setGradeLoading] = useState(false)
	const [accountModalState, setAccountModalState] = useAtom(accountModalAtom)
	const [justCopied, setJustCopied] = useState(false)

	const docID = useDocId()
	const { currentUser } = useAuth() as any
	const documentState: DocumentState | null = useSelector((state) => selectDocumentState(state, docID))
	const user = useSelector(selectUser)

	const handleGrade = useCallback(async () => {
		if (gradeResult) {
			return // Don't refetch if we already have results
		}
		setGradeLoading(true)

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: currentUser ? `Bearer ${await currentUser.getIdToken()}` : '',
			},
			body: JSON.stringify({
				isV2: false, // Use the old version to get contentAndIdeasScores and thesisStatement
				topic: htmlToString(documentState?.title ?? ''),
				essay: htmlToString(documentState?.content ?? ''),
				userId: user.id,
			}),
		}

		try {
			const response = await fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/grade/', requestOptions)
			const result: GraderRubric = await response.json()
			setGradeResult(result)
		} catch (err) {
			console.error('Grade API error:', err)
			Sentry.captureException(err)
		} finally {
			setGradeLoading(false)
		}
	}, [gradeResult, currentUser, documentState, user])

	useEffect(() => {
		if (defaultPlagiarismTabFF === 'test') {
			setOpenBox(0)
		}
	}, [defaultPlagiarismTabFF])

	useEffect(() => {
		if (open) {
			setLoading(true)
			// Start grading immediately when modal opens
			handleGrade()
		} else {
			// Reset grading state when modal closes
			setGradeResult(null)
			setGradeLoading(false)
		}
	}, [open, handleGrade])

	// Keep main loading state synced with grade loading
	useEffect(() => {
		setLoading(gradeLoading)
	}, [gradeLoading])

	const toggleBox = (index: number) => {
		setOpenBox(openBox === index ? null : index)
	}

	const contentAndIdeasScores = [
		{
			title: 'Relevance',
			description:
				'The content score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: gradeResult?.relevanceToTopic ?? 50,
		},
		{
			title: 'Depth of Analysis',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: gradeResult?.depthOfAnalysis ?? 50,
		},
		{
			title: 'Evidence and Support',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: gradeResult?.evidenceAndSupport ?? 50,
		},
		{
			title: 'Consistent Tone',
			description:
				'The content score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: gradeResult?.consistencyOfTone ?? 50,
		},
	]

	const ScoreBarLocal = ({ score }: { score: number }) => {
		return (
			<div className="flex gap-1 items-center">
				<ScoreBar score={score} />
				<div className="text-xs">{score}</div>
			</div>
		)
	}

	const divider = <div className="border-b border-gray-300" />

	return (
		<Modal open={open} closeModal={closeModal} portalClassName="min-w-6xl w-screen max-w-6xl">
			<div className="md:w-[600px] w-full p-4">
				{loading ? (
					<>
						<div className="font-bold text-lg">Analyzing Essay...</div>
						<div className="flex flex-col gap-2 mt-6 items-center">
							{LuLoaderCircle({ className: 'animate-spin w-10 h-10 text-link mx-auto' })}
							<div className="text-lg font-semibold mx-auto mt-3">Processing your essay</div>
							<div className="text-sm text-gray-700 text-center">
								{isPremium
									? 'Checking plagiarism and generating grade...'
									: 'Checking plagiarism, Al detection, and generating grade...'}
							</div>
						</div>
						<div className="flex flex-col gap-2 mt-6">
							{Array.from({ length: isPremium ? 2 : 3 }).map((_, i) => (
								<div key={i} className="border border-gray-200 rounded p-2 flex flex-col">
									<div className="flex items-center gap-2 -mb-2">
										<Skeleton variant="rounded" width={25} height={25} />
										<Skeleton variant="text" width={100} height={40} />
									</div>
									<Skeleton variant="text" width="100%" height={35} />
									<Skeleton variant="text" width="75%" height={35} />
								</div>
							))}
						</div>
					</>
				) : (
					<>
						<div className="font-bold text-lg mb-6">Essay Analysis Complete</div>
						<div className="flex flex-col gap-2">
							{[
								{
									title: 'Plagiarism Check',
									icon: LuShield,
									color: '#a103fc',
									badgeText: '0 Matches',
									expandedContent: (
										<div className="space-y-4">
											<p className="text-sm text-muted-foreground">
												Excellent originality. No significant plagiarism detected.
											</p>

											<div className="space-y-3">
												<div className="flex justify-between text-sm">
													<span className="text-black">Sources Checked:</span>
													<span className="text-muted-foreground">2.1 billion web pages</span>
												</div>
												<div className="flex justify-between text-sm">
													<span className="text-black">Academic Papers:</span>
													<span className="text-muted-foreground">70 million documents</span>
												</div>
												<div className="flex justify-between text-sm">
													<span className="text-black">Student Papers:</span>
													<span className="text-muted-foreground">500 million submissions</span>
												</div>
											</div>

											<div className="bg-gray-100 p-3 rounded-md">
												<h4 className="font-medium text-sm mb-2 text-gray-900">Detection Results:</h4>
												<div className="space-y-2 text-sm">
													<div className="flex items-center gap-2">
														{LuCircleCheck({ className: 'h-4 w-4 text-success-default' })}
														<span>No exact matches found</span>
													</div>
													<div className="flex items-center gap-2">
														{LuCircleCheck({ className: 'h-4 w-4 text-success-default' })}
														<span>Proper citation format detected</span>
													</div>
													<div className="flex items-center gap-2">
														{LuCircleCheck({ className: 'h-4 w-4 text-success-default' })}
														<span>Original analysis and interpretation</span>
													</div>
												</div>
											</div>
										</div>
									),
								},
								...(!isPremium
									? [
											{
												title: 'AI Detection',
												icon: LuScanText,
												color: '#0390fc',
												badgeText: '100% Human',
												expandedContent: (
													<div className="space-y-4">
														<p className="text-sm text-muted-foreground">
															This essay shows strong indicators of human authorship. The writing style, sentence
															structure, and content flow are consistent with human-generated text.
														</p>

														<div className="space-y-3">
															<div className="flex justify-between items-center">
																<span className="text-sm font-semibold text-black">
																	Third-Party AI Detector Scores:
																</span>
																<div className="flex gap-3 text-sm">
																	<div className="flex items-center gap-1 font-semibold text-success-default">
																		{LuCircleCheck({ className: 'h-3 w-3' })}
																		Human
																	</div>
																</div>
															</div>
														</div>

														<div className="bg-gray-100 p-3 rounded-md">
															<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
																{[
																	{ name: 'Turnitin', score: 0 },
																	{ name: 'GPTZero', score: 0 },
																	{ name: 'Copyleaks', score: 0 },
																	{ name: 'Sapling', score: 0 },
																	{ name: 'ContentAtScale', score: 0 },
																	{ name: 'ZeroGPT', score: 0 },
																].map((detector) => (
																	<div
																		key={detector.name}
																		className="flex items-center justify-between bg-white p-2 rounded border"
																	>
																		<div className="flex items-center gap-2">
																			{LuCircleCheck({ className: 'h-4 w-4 text-success-default' })}
																			<span className="text-sm font-medium text-gray-900">{detector.name}</span>
																		</div>
																	</div>
																))}
															</div>
														</div>
													</div>
												),
											},
									  ]
									: []),
								{
									title: 'Grade Analysis',
									icon: LuBookCheck,
									color: '#40bf00',
									badgeText: gradeResult
										? `${Math.floor(
												(((gradeResult?.relevanceToTopic ?? 0) +
													(gradeResult?.depthOfAnalysis ?? 0) +
													(gradeResult?.evidenceAndSupport ?? 0) +
													(gradeResult?.consistencyOfTone ?? 0) +
													(gradeResult?.correctGrammar ?? 0) +
													(gradeResult?.correctSpelling ?? 0) +
													(gradeResult?.goodWordChoice ?? 0) +
													(gradeResult?.qualityOfParagraphTransitions ?? 0)) /
													8) *
													10
										  )} / 100`
										: '-- / 100',
									expandedContent: gradeLoading ? (
										<div className="flex flex-col gap-2 items-center py-4">
											{LuLoaderCircle({ className: 'animate-spin w-8 h-8 text-link mx-auto' })}
											<div className="text-sm font-semibold">Analyzing your essay...</div>
											<div className="text-xs text-gray-600">This may take a few moments</div>
										</div>
									) : gradeResult ? (
										<div className="flex flex-col gap-4">
											{/* Constructive Comments */}
											<div className="flex flex-col gap-1">
												<div className="text-sm font-semibold">Overall Assessment:</div>
												<div className="text-sm text-gray-700">{gradeResult.constructiveComments}</div>
											</div>

											{/* Content & Ideas */}
											<div className="flex flex-col gap-3">
												<div className="text-sm font-semibold">Content & Ideas</div>
												{divider}
												{contentAndIdeasScores.map((item, index) => (
													<div key={index} className="flex flex-col gap-2">
														<div className="flex gap-3 justify-between items-center">
															<div className="text-sm font-semibold">{item.title}:</div>
															<ScoreBarLocal score={item.score} />
														</div>
													</div>
												))}
											</div>

											{/* Thesis Statement */}
											<div className="flex flex-col gap-1">
												<div className="text-sm font-semibold">Thesis Statement:</div>
												<div className="text-sm text-gray-700">{gradeResult.thesisStatement}</div>
											</div>
										</div>
									) : (
										<div className="text-sm text-gray-700">Click to analyze your essay and get detailed feedback.</div>
									),
								},
							].map((item, index) => (
								<div
									key={index}
									className="border border-gray-200 py-4 px-4 rounded-lg cursor-pointer shadow"
									onClick={() => toggleBox(index)}
								>
									<div className="flex justify-between gap-2">
										<div className="flex items-center gap-2">
											{item.icon({ className: `text-[${item.color}] w-5 h-5`, style: { color: item.color } })}
											<div className="text-lg font-semibold">{item.title}</div>
										</div>
										<div className="flex items-center gap-2">
											<div className="text-success-dark px-2 py-1 rounded-full font-bold text-sm text-right">
												{item.badgeText}
											</div>
											{LuChevronRight({
												className: `text-gray-500 w-6 h-6 transition-transform duration-200 ${
													openBox === index ? 'rotate-90' : ''
												}`,
											})}
										</div>
									</div>
									{openBox === index && (
										<div className="mt-4 pt-4 border-t border-gray-200">
											<p className="text-gray-700 text-sm leading-relaxed">{item.expandedContent}</p>
										</div>
									)}
								</div>
							))}
							<div className="my-4 border-t border-gray-300"></div>
							<div className="border py-4 px-4 rounded-lg flex flex-col bg-gray-100 mb-4">
								<div className="flex justify-between">
									<div className="flex items-center gap-2">
										{LuShare({ className: 'w-5 h-5 text-gray-900' })}
										<div className="text-base font-semibold">Export Essay</div>
									</div>
									<Button
										type="blue"
										className="px-4 py-2 text-sm font-semibold"
										onClick={() => {
											navigator.clipboard.writeText(
												htmlToString(documentState?.content ?? '') +
													((documentState?.references?.length ?? 0) > 0
														? '\n\nReferences\n' +
														  documentState?.references
																.map((ref, index) =>
																	formatReferenceAsString(documentState.userWriterSelections.refStyle!, ref)
																)
																.join('\n')
														: '')
											)
											setJustCopied(true)
											setTimeout(() => setJustCopied(false), 1000)
										}}
									>
										{justCopied
											? LuCheck({ className: 'w-4 h-4 text-white inline-block mr-2' })
											: LuCopy({ className: 'w-4 h-4 text-white inline-block mr-2' })}
										Copy to Clipboard
									</Button>
								</div>
								{!isPremium && (
									<div className="rounded border border-[#f49e0a] bg-[#f49e0a]/10 p-4 mt-4 text-sm text-warning-dark flex flex-col gap-2">
										<div className="flex items-center gap-2 font-medium">
											{LuLock({ className: 'w-4 h-4' })}
											<div>
												<span
													className="underline cursor-pointer"
													onClick={() => {
														closeModal()
														if (completeToSignupFF === 'test') {
															setAccountModalState({
																...accountModalState,
																loginModalOpen: true,
																unlockEssay: true,
															})
														} else {
															setAccountModalState({
																...accountModalState,
																unlockEssay: true,
																subscriptionModalOpen: true,
															})
														}
													}}
												>
													Upgrade to premium
												</span>{' '}
												to export your full essay.
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</>
				)}
			</div>
		</Modal>
	)
}

export default CompleteModal
